export default function Incubation() {
  const timeline = [
    { date: "Phase 1", title: "Conceptualization", desc: "Identified critical gaps in cucumber seed germination tracking within rural nursery setups." },
    { date: "Phase 2", title: "iHub Induction", desc: "Formally inducted into the AWaDH IIT Ropar virtual incubation program for technical acceleration." },
    { date: "Phase 3", title: "Alpha Testing", desc: "Successful validation of the non-invasive seed quality assessment prototype in laboratory settings." },
    { date: "Phase 4", title: "Scale & Impact", desc: "Expanding pilot programs to commercial nurseries across the northern Indian agricultural belt." },
  ];

  return (
    <>
      {/* Hero */}
      <section className="relative py-16 md:py-24 overflow-hidden px-4 md:px-margin-desktop max-w-container-max mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 bg-secondary-container text-on-secondary-container px-3 py-1 rounded-full">
              <span className="material-symbols-outlined text-sm">verified_user</span>
              <span className="font-label-mono text-label-mono uppercase">Strategic Partnership</span>
            </div>
            <h1 className="font-headline-xl text-headline-xl text-data-teal leading-tight">
              Cultivating Innovation with <span className="text-primary">iHub-AWaDH</span>
            </h1>
            <p className="font-body-lg text-body-lg text-on-surface-variant max-w-2xl">
              CAFE is proud to be part of the virtual incubation program at IIT Ropar. This collaboration bridges academic excellence with entrepreneurial agility to redefine seed quality assessment.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-3 bg-surface-container-low p-4 rounded-xl border-l-4 border-growth-green">
                <span className="material-symbols-outlined text-primary text-3xl">school</span>
                <div>
                  <p className="font-label-bold text-label-bold text-on-surface-variant uppercase">Academic Partner</p>
                  <p className="font-headline-md text-headline-md text-data-teal">IIT Ropar</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-surface-container-low p-4 rounded-xl border-l-4 border-cucumber-blossom">
                <span className="material-symbols-outlined text-primary text-3xl">biotech</span>
                <div>
                  <p className="font-label-bold text-label-bold text-on-surface-variant uppercase">Ecosystem</p>
                  <p className="font-headline-md text-headline-md text-data-teal">AWaDH iHub</p>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[2/1] rounded-2xl overflow-hidden shadow-xl border border-outline-variant/30 bg-white p-8 flex items-center justify-center">
              <img
                alt="IIT Ropar AWaDH Logo"
                className="max-w-full h-auto object-contain"
                src="/IIT-Ropar-AWaDH.webp"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-data-teal text-white p-6 rounded-2xl shadow-lg hidden md:block max-w-[240px]">
              <p className="font-label-mono text-label-mono text-cucumber-blossom mb-2">VIRTUAL INCUBATION</p>
              <p className="font-body-sm text-body-sm opacity-90">Access to world-class computational labs and technical expertise in Agriculture and Water Technology.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mentorship & Support */}
      <section className="bg-surface-container-low py-20 px-4 md:px-margin-desktop">
        <div className="max-w-container-max mx-auto">
          <div className="mb-12">
            <h2 className="font-headline-lg text-headline-lg text-data-teal mb-4">Mentorship & Strategic Support</h2>
            <p className="font-body-md text-body-md text-on-surface-variant max-w-2xl">Leveraging the intellectual capital of IIT Ropar to ensure our technological solutions are scientifically grounded and commercially viable.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-gutter">
            <div className="tonal-layer-1 p-8 rounded-2xl border border-outline-variant/10 flex flex-col gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <span className="material-symbols-outlined text-primary">analytics</span>
              </div>
              <h3 className="font-headline-md text-headline-md text-data-teal">Technical Validation</h3>
              <p className="font-body-md text-body-md text-on-surface-variant">Rigorous peer-review of seed assessment algorithms by data scientists and agricultural experts at AWaDH.</p>
              <div className="mt-auto pt-4 border-t border-outline-variant/20">
                <span className="font-label-mono text-label-mono text-growth-green">HIGH ACCURACY GOAL</span>
              </div>
            </div>
            <div className="tonal-layer-1 p-8 rounded-2xl border border-outline-variant/10 flex flex-col gap-4">
              <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center">
                <span className="material-symbols-outlined text-secondary">precision_manufacturing</span>
              </div>
              <h3 className="font-headline-md text-headline-md text-data-teal">Product Refinement</h3>
              <p className="font-body-md text-body-md text-on-surface-variant">Iterative hardware design for field-ready sensors, ensuring durability in diverse climatic conditions of India.</p>
              <div className="mt-auto pt-4 border-t border-outline-variant/20">
                <span className="font-label-mono text-label-mono text-earth-clay">IP-67 COMPLIANT DESIGN</span>
              </div>
            </div>
            <div className="tonal-layer-1 p-8 rounded-2xl border border-outline-variant/10 flex flex-col gap-4">
              <div className="w-12 h-12 bg-tertiary/10 rounded-lg flex items-center justify-center">
                <span className="material-symbols-outlined text-tertiary">hub</span>
              </div>
              <h3 className="font-headline-md text-headline-md text-data-teal">Ecosystem Networking</h3>
              <p className="font-body-md text-body-md text-on-surface-variant">Connecting CAFE with a vast network of FPOs, seed companies, and governmental bodies for pilot deployments.</p>
              <div className="mt-auto pt-4 border-t border-outline-variant/20">
                <span className="font-label-mono text-label-mono text-primary">GROWING NETWORK</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Growth Journey Timeline */}
      <section className="py-24 px-4 md:px-margin-desktop max-w-container-max mx-auto overflow-hidden">
        <h2 className="font-headline-lg text-headline-lg text-data-teal mb-16 text-center">Our Growth Journey</h2>
        <div className="relative">
          <div className="absolute top-1/2 left-0 w-full h-1 bg-seed-husk -translate-y-1/2 hidden lg:block"></div>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-gutter relative z-10">
            {timeline.map((step, i) => (
              <div key={step.title} className={`bg-white p-6 rounded-2xl border border-outline-variant/30 ${i % 2 === 0 ? "lg:-translate-y-[20%]" : "lg:translate-y-[20%]"}`}>
                <div className="font-label-mono text-label-mono text-growth-green mb-2">{step.date}</div>
                <h4 className="font-headline-md text-headline-md text-data-teal mb-3">{step.title}</h4>
                <p className="font-body-sm text-body-sm text-on-surface-variant">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 md:px-margin-desktop max-w-container-max mx-auto">
        <div className="bg-data-teal rounded-[2rem] p-8 md:p-16 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-cucumber-blossom/10 rounded-full blur-3xl -ml-32 -mb-32"></div>
          <div className="relative z-10 max-w-3xl mx-auto space-y-8">
            <h2 className="font-headline-xl text-headline-xl text-white">Join the Agri-Tech Revolution</h2>
            <p className="font-body-lg text-body-lg text-secondary-fixed opacity-90">
              We are actively seeking research collaborators, seed scientists, and industry partners to expand our impact. Let's build a more precise agricultural future together.
            </p>
            <div className="flex flex-col md:flex-row gap-6 justify-center pt-4">
              <button className="bg-cucumber-blossom text-on-secondary-fixed font-headline-md px-10 py-4 rounded-xl hover:scale-105 transition-transform">
                Apply for Partnership
              </button>
              <button className="border border-secondary-fixed text-secondary-fixed font-headline-md px-10 py-4 rounded-xl hover:bg-white/10 transition-colors">
                View Research Papers
              </button>
            </div>
            <p className="font-label-mono text-label-mono text-white/60 pt-4">
              Supported by Department of Science & Technology, Govt. of India
            </p>
          </div>
        </div>
      </section>

      {/* Metric Chips */}
      <section className="pb-24 px-4 md:px-margin-desktop max-w-container-max mx-auto">
        <div className="flex flex-wrap justify-center gap-4">
          {[
            { label: "GROWING STAKEHOLDERS", color: "growth-green" },
            { label: "BATCHES SCANNED", color: "earth-clay" },
            { label: "RESEARCH CITATIONS", color: "primary" },
            { label: "PATENTS PENDING", color: "cucumber-blossom" },
          ].map((chip) => (
            <div key={chip.label} className={`bg-${chip.color}/10 px-4 py-2 rounded-full flex items-center gap-2 border border-${chip.color}/20`}>
              <span className={`font-label-mono text-label-mono text-${chip.color}`}>{chip.label}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
